import moment from "moment";
import { useEffect, useMemo, useRef } from "react";
import { useDraggable } from "react-use-draggable-scroll";
import { AvailableReservations, ClosedDay, ClosedReason, ScheduleData } from "src/api/reservation/reservation-type";
import SwiperCalendar from "src/components/swiperCalendar/SwiperCalendar";
import { useToast } from "src/pages/hooks/toast";
import { minutesToDisplayText, numberToStringWithComma } from "src/utils/common-util";

type Props = {
  date: string;
  selectedDate: (date: string) => void;
  scheduleSlots: ScheduleData[];
  onSelectSchedules: (data: ScheduleData) => void;
  selectedSchedule: ScheduleData[];
  onCalendarRangeChange: (startDate: string, endDate: string) => void;
  closedDays: ClosedDay[];
  onResetSchedule: () => void;
  reserveTimeValidation: {
    minTime: { message: string; isDisabled: boolean };
    maxTime: { message: string; isDisabled: boolean };
    availableTimes: { message: string; isDisabled: boolean };
  };
  updatedAt: string;
  onRefresh: () => void;
  reserveDuration?: AvailableReservations; // 예약 가능 기간 설정
};

const FacilityReservation = ({
  date,
  selectedDate,
  scheduleSlots,
  onSelectSchedules,
  selectedSchedule,
  onCalendarRangeChange,
  closedDays,
  onResetSchedule,
  reserveTimeValidation,
  updatedAt,
  onRefresh,
  reserveDuration,
}: Props) => {
  const slotRef = useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLInputElement>; //포커스를 위한 ref
  const calendarRef = useRef<HTMLDivElement>(null);
  // 시간 설정 Slot에서 drag 로 스크롤 이동 적용
  const { events } = useDraggable(slotRef); // Now we pass the reference to the useDraggable hook:

  // 스케줄 예약 최소/최대 시간 확인 api

  //유저의 최소/최대 사용시간 노출

  useEffect(() => {
    scrollToCurrentSlot();
  }, [scheduleSlots]);

  // 현재 시간
  const calendarDate = useMemo(() => {
    const todayAndTime = moment().toDate();
    const _calendarDate = moment(date).toDate();

    if (moment(todayAndTime).isSame(_calendarDate, "D")) {
      return todayAndTime;
    } else {
      return _calendarDate;
    }
  }, [date]);

  const { openToast } = useToast();

  const scrollToCurrentSlot = () => {
    if (slotRef.current) {
      const findTimeRow = scheduleSlots.find(
        (item) =>
          moment(item.startDate).isBefore(calendarDate) && //
          moment(item.endDate).isAfter(calendarDate),
      )?.slot;

      //findTimeRow 는 오늘 날짜, 현재시간 기준의 slot 번호 / 17은 오늘 이후 날짜로 오전 8시 기준 슬롯 번호
      const currentWidth = (Number(findTimeRow ? findTimeRow : 17) - 1) * 46.99;
      const friendsContainer = slotRef.current;

      if (friendsContainer) {
        friendsContainer.scrollTo({
          top: 0,
          left: currentWidth,
          behavior: "smooth",
        });
      }
    }
  };

  // 현재 시간 기준으로 스크롤 이동

  const reservationHours = useMemo(() => {
    const list = scheduleSlots.map((item) => moment(item.startDate).format("HH"));
    const uniqueData: string[] = [];
    const seenIndices: Record<string, number> = {};

    for (let i = 0; i < list.length; i++) {
      const value: string = list[i];

      if (seenIndices[value] === undefined) {
        // 해당 값이 처음 나타난 경우
        seenIndices[value] = i;
        uniqueData.push(value);
      } else {
        // 이미 나온 값이라면 현재 값은 ""(빈 문자열)으로 처리
        uniqueData[i] = "";
      }
    }
    return uniqueData;
  }, [scheduleSlots]);

  // 예약 가능 기간 설정에 따른 안내 문구
  const availablePeriodMessage = useMemo(() => {
    if (!reserveDuration?.id || !reserveDuration.isLimitedAvailableReservationTime) return "";

    const startTime = Number(reserveDuration.availableReservationTimeStart);
    const endTime = Number(reserveDuration.availableReservationTimeEnd);

    if (startTime > 0 && endTime > 0) {
      return `현재 시점부터 ${minutesToDisplayText(startTime)} 이후~${minutesToDisplayText(endTime)} 이내 예약할 수 있습니다.`;
    }
    if (startTime > 0) {
      return `현재 시점부터 ${minutesToDisplayText(startTime)} 이후에 예약할 수 있습니다.`;
    }
    if (endTime > 0) {
      return `현재 시점부터 ${minutesToDisplayText(endTime)} 이내에 예약할 수 있습니다.`;
    }
    return "";
  }, [reserveDuration]);

  // 선택 불가한 slot 의 사유별 안내 문구
  const getClosedMessage = (closedReason: ClosedReason, schedule: ScheduleData) => {
    switch (closedReason) {
      case "OUT_OF_SALE_PERIOD":
        return "판매 가능 기간이 아닙니다.";
      case "PAST":
        return "과거 시간은 예약할 수 없습니다.";
      case "RESERVED":
        return "이미 예약된 시간입니다.";
      case "CLOSED_PERIOD":
        // 휴무 사유가 설정된 경우에만 사유를 노출한다
        return schedule.closedTitle || "휴무 시간은 예약할 수 없습니다.";
      case "OUT_OF_PERIOD":
        return availablePeriodMessage;
      default:
        return "";
    }
  };

  // 복수 사유 위반 시 우선순위 상위 1개만 노출하고 나머지는 개수로 안내
  const getClosedToastMessage = (schedule: ScheduleData) => {
    const [primaryReason, ...restReasons] = schedule.closedReasons || [];
    if (!primaryReason) return "";

    const primaryMessage = getClosedMessage(primaryReason, schedule);
    if (!primaryMessage) return "";

    return restReasons.length > 0 ? `${primaryMessage} 외 ${restReasons.length}개` : primaryMessage;
  };

  const onClickTimes = (schedule: ScheduleData) => {
    if (schedule.isClosed) {
      // 선택 불가 사유를 토스트로 안내
      const closedMessage = getClosedToastMessage(schedule);
      if (closedMessage) openToast({ content: closedMessage });
      return;
    }

    const isAvailable =
      (moment(schedule.startDate).isBefore(calendarDate) && moment(schedule.endDate).isAfter(calendarDate)) ||
      moment(schedule.startDate).isSameOrAfter(calendarDate);

    // slot 생성 이후 시간이 흘러 과거가 된 경우
    if (!isAvailable) {
      openToast({ content: "과거 시간은 예약할 수 없습니다." });
      return;
    }

    onSelectSchedules(schedule);
  };

  return (
    <section className="facility-info__calendar-time">
      <div
        ref={calendarRef}
        className="calendar-area" //
      >
        <h2>날짜를 선택해 주세요</h2>
        <SwiperCalendar
          onCalendarRangeChange={onCalendarRangeChange}
          onDateClick={(date) => {
            selectedDate(date);
            onResetSchedule();
          }}
          closedDays={closedDays}
        />
      </div>
      <div className="time-area">
        <div className="time-area__head">
          <h2 className="pl0">시간을 선택해 주세요</h2>
          <div className="time-area__update">
            {updatedAt && <span>{`${updatedAt} 업데이트`}</span>}
            <button
              type="button" //
              className="time-area__update-btn"
              onClick={onRefresh}
              aria-label="예약 시간 새로고침"
            />
          </div>
        </div>
        <p className="time-area__desc">좌우로 넘겨 시간을 선택하세요</p>
        <div
          className="time-picker" //
          ref={slotRef} // 스크롤 최상단 위치, 시간 선택 부분만 스크롤 할거면 여기 사용
          {...events}
        >
          <div className="time-picker__index">
            {reservationHours.map((hour, idx) => (
              <span
                key={idx} //
                className={`ml3 ${Number(moment(calendarDate).format("HH")) > Number(hour) ? "text-primary4" : ""}`}
              >
                {hour}
              </span>
            ))}
          </div>
          <div className="time-picker__index">
            {reservationHours.map((hour, idx) => (
              <div key={idx}></div>
            ))}
          </div>
          <div id="time-picker__grid" className="time-picker__grid">
            {scheduleSlots.map((item) => {
              // 과거 경과 시간만 해당하는 slot 은 gray 영역 없이 기본 스타일(요금 노출) 유지
              const isPastOnlySlot = !!item.closedReasons?.length && item.closedReasons.every((reason) => reason === "PAST");

              return (
                <div
                  className={
                    selectedSchedule.find((time) => time.slot === item.slot)
                      ? "--active"
                      : item.isClosed === true && !isPastOnlySlot //
                      ? "--disabled"
                      : ""
                  }
                  key={item.slot}
                  onClick={() => {
                    onClickTimes(item);
                  }}
                >
                  {(!item.isClosed || isPastOnlySlot) && numberToStringWithComma(item.price)}
                </div>
              );
            })}
          </div>
        </div>
        {/* {validateText && <p className="mt10 font13 text-red">{validateText}</p>} */}
        {/* 유효성 메시지는 위반된 항목(빨간 X)만 노출 */}
        {/* 최소 예약시간 메시지 */}
        {reserveTimeValidation.minTime.message && reserveTimeValidation.minTime.isDisabled && (
          <p className="mt10 disable-validation">{reserveTimeValidation.minTime.message}</p>
        )}
        {/* 최대 예약시간 메시지 */}
        {reserveTimeValidation.maxTime.message && reserveTimeValidation.maxTime.isDisabled && (
          <p className="mt10 disable-validation">{reserveTimeValidation.maxTime.message}</p>
        )}
        {/* 예약가능 시간 메세지 */}
        {reserveTimeValidation.availableTimes.message && reserveTimeValidation.availableTimes.isDisabled && (
          <p className="mt10 disable-validation">{reserveTimeValidation.availableTimes.message}</p>
        )}
      </div>
    </section>
  );
};

export default FacilityReservation;
